import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './index.module.css'
import { useCheckLogin, useDebounce, useHeightTransition } from '../../../../utils/hooks'
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { createStory, getStoriesInPublicGallery } from '../../../../utils/net';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { logInfoAtom, personalStoriesAtom, storyTagsAtom } from '../../../../store';
import * as Sentry from "@sentry/react";
// common
import { useTips } from '../../../../common/GlobalTips';
import { CSSLoading } from '../../../../common/CSSLoading';

// assets
import story_youtube from '../../assets/story_youtube_small.png'
import story_twitter from '../../assets/story_twitter_small.png'
import story_bilibili from '../../assets/story_bilibili_small.png'
import story_ins from '../../assets/story_ins_small.png'
import story_pinterest from '../../assets/story_pinterest_small.png'
import story_reddit from '../../assets/story_reddit_small.png'
import story_xiaohongshu from '../../assets/story_xiaohongshu_small.png'
import story_noresult from '../../assets/story_noresult.png'
import story_pending from '../../assets/story_pending.png'
import story_rejected from '../../assets/story_rejected.png'
import story_cursor from '../../assets/story_cursor.png'
import { detectTypeFromUrl } from '../../../../utils/format';

const STORY_MEDIA_MAP = [
  { type: 'YouTube', icon: story_youtube, permission: true },
  { type: 'X', icon: story_twitter, permission: true },
  { type: 'Bilibili', icon: story_bilibili, permission: true },
  { type: 'Xiaohongshu', icon: story_xiaohongshu, permission: true },
  { type: 'Reddit', icon: story_reddit, permission: true },
  { type: 'Ins', icon: story_ins, permission: true },
  { type: 'Pinterest', icon: story_pinterest, permission: false },
]

const StoryPost = () => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const tip = useTips();
  const { checkLogin } = useCheckLogin();
  const [isHovered, setIsHovered] = useState(true);
  const [loading, setLoading] = useState(false);
  const setPersonalStories = useSetRecoilState(personalStoriesAtom)
  const storyTags = useRecoilValue(storyTagsAtom);

  const fetchPersonalStories = async () => {
    const stories = await getStoriesInPublicGallery({
      access_token: localStorage.getItem('token'),
      status: 'All',
      tagNames: [...storyTags]
    });

    // 处理 Unknown 类型
    if (stories?.storyList) {
      stories.storyList = stories.storyList.map(story => {
        if (story.type === 'Unknown') {
          return {
            ...story,
            type: detectTypeFromUrl(story.url)
          };
        }
        return story;
      });
    }
    setPersonalStories(stories);
  };

  useEffect(() => {
    const handleCloseStoryPost = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        // setIsHovered(false);
      }
    }
    document.addEventListener('mousedown', handleCloseStoryPost);
    return () => {
      document.removeEventListener('mousedown', handleCloseStoryPost);
    };
  }, []);

  // useEffect(() => {
  //   const hoverTimeOut = setTimeout(() => {
  //     if (isHovered) {
  //       inputRef.current?.focus();
  //     } else {
  //       inputRef.current?.blur();
  //     }
  //   }, 300);
  //   return () => clearTimeout(hoverTimeOut);
  // }, [isHovered])

  const handleMouseEnter = () => {
    if (inputRef.current) {
      setIsHovered(true)
    }
  };

  const handleMouseLeave = () => {
    if (inputRef.current) {
      // setIsHovered(false)
    }
  };

  const handleCreateStory = async () => {
    const video_raw_url = inputRef.current?.value;
    if (!checkLogin()) return
    if (!video_raw_url) {
      tip({
        type: 'warning',
        content: 'Please enter a URL to be shared.',
      });
      return
    }

    // 判断是否为合法网址
    const isValidUrl = (url) => {
      try {
        new URL(url);
        return true;
      } catch (err) {
        return false;
      }
    }

    if (!isValidUrl(video_raw_url)) {
      tip({
        type: 'warning',
        content: 'Please enter a valid URL.',
      });
      return;
    }

    // 判断是否为支持的平台
    const supportedPlatforms = {
      'youtube.com': true,
      'youtu.be': true,
      'twitter.com': true,
      'x.com': true,
      'bilibili.com': true,
      'xiaohongshu.com': true,
      'reddit.com': true,
      'instagram.com': true,
    };

    const urlDomain = new URL(video_raw_url).hostname.replace('www.', '');
    if (!Object.keys(supportedPlatforms).some(domain => urlDomain.includes(domain))) {
      tip({
        type: 'warning',
        content: 'This platform is not supported yet. Please share content from YouTube, X(Twitter), Bilibili or Xiaohongshu.',
      });
      return;
    }

    try {
      setLoading(true);
      const result = await createStory(video_raw_url)
      if (!result?.error) {
        tip({
          type: 'success',
          content: 'Successfully created a story! Please wait for review.'
        });
      } else {
        throw new Error(result?.error)
      }
    } catch (error) {
      tip({
        type: 'error',
        content: 'Failed to create a story, Please check the URL and try again.'
      });
    } finally {
      fetchPersonalStories()
      setLoading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`flex flex-col items-center justify-between overflow-hidden rounded-2xl
      ${isHovered ? "p-[16px_16px_12px_30px]" : "p-[8px_36px_24px_30px] sm:p-[8px_36px_18px_30px]"}   
      ${styles.shareWrapper}`}>
      <div className={`flex flex-col items-start justify-around  h-[calc(100%-16px)] w-[50%] absolute-y-center left-[30px] transition-300-ease ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
        <p className={` text-[14px] sm:text-[16px] md:text-[18px] font-bold text-white text-left`}>
          Recommend <span className="[background-image:linear-gradient(90deg,#E9B664_44.89%,#8C76F0_92.99%)] [color:transparent] [-webkit-background-clip:text]"> Rodin-related </span> posts for <span className="[background-image:linear-gradient(90deg,#E9B664_44.89%,#8C76F0_92.99%)] [color:transparent] [-webkit-background-clip:text]"> bonus credits!</span>
        </p>
        <div className='flex items-center gap-2 w-full'>
          <span className='text-[14px] text-[rgba(255,255,255,0.4)] mr-[8px]'>Support</span>
          <div className='flex-1 flex items-center gap-0 xs:gap-2'>
            {STORY_MEDIA_MAP.map((item, index, arr) => {
              if (item.permission) {
                return (
                  <i key={item.type} className='px-2 xs:px-4 relative'>
                    <img src={item.icon} alt={item.type} className="h-[20px] w-auto" />
                    {index !== arr.filter(i => i.permission).length - 1 && (
                      <span className='absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-1/2 bg-[rgba(255,255,255,0.2)]'></span>
                    )}
                  </i>
                )
              }
            })}
          </div>

        </div>
      </div>
      {/* Top Part*/}
      <div className='w-full flex items-center justify-between'>
        <div className={`w-[267px] sm:w-[477px] h-full flex items-center justify-start text-[14px] sm:text-[16px] md:text-[18px] font-bold text-white text-left
        ${isHovered ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
          <p >You will receive <span className="[background-image:linear-gradient(90deg,#E9B664_44.89%,#8C76F0_92.99%)] [color:transparent] [-webkit-background-clip:text]"> 1 credits </span> for each post you recommend after our moderation!</p>
        </div>
        <div
          className={`group/wrapper p-2 flex will-change-auto  justify-between items-center origin-right [transition:transform_0.3s_ease] w-[45%] rounded-lg bg-[rgba(255,255,255,0.12)]
           ${isHovered ? 'rotate-0 scale-100' : 'rotate-[-10deg] scale-90 [mask:linear-gradient(90deg,transparent,#000)]'}`}
        >
          <input
            ref={inputRef}
            className="pl-[8px] text-white bg-transparent outline-none w-full h-full"
            placeholder="Paste the link here"
            type="text"
          />
          <button className={`relative text-white rounded p-[4px_16px] sm:p-[6px_18px] md:p-[8px_22px] text-[14px] sm:text-[16px] font-bold hover:[box-shadow:0px_0px_12px_0px_rgba(255,206,115,0.25)] transition-300-ease
          ${styles.shareButton}`}
            onClick={handleCreateStory}>
            <img src={story_cursor} alt="story_cursor" className={`w-[24px] h-auto absolute bottom-[-12px] right-1 transition-300-ease ${isHovered ? 'opacity-0' : 'opacity-100'}`} />
            <span className={`transition-300-ease ${!loading ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>Share</span>
            <div className={`${loading ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-300-ease absolute-center`}>
              <CSSLoading />
            </div>
          </button>
        </div>
      </div>
      {/* Bottom Part*/}
      <div className={`w-full flex justify-between items-center gap-2 ${isHovered ? 'opacity-100 h-[40px]' : 'opacity-0 h-0'} transition-300-ease`}>
        <span className='text-[12px] sm:text-[14px] text-[rgba(255,255,255,0.4)] mr-[8px] text-left'>Share any Rodin-related posts even if they aren&apos;t from you.</span>
        <div className='flex items-center'>
          {STORY_MEDIA_MAP.map((item, index, arr) => {
            if (item.permission) {
              return (
                <i key={item.type} className='px-2 sm:px-4 relative'>
                  <img src={item.icon} alt={item.type} className="h-[20px] w-auto" />
                  {index !== arr.filter(i => i.permission).length - 1 && (
                    <span className='absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-1/2 bg-[rgba(255,255,255,0.2)]'></span>
                  )}
                </i>
              )
            }
          })}
        </div>
      </div>
    </div>
  );
};

const MiniStoryPost = () => {
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { checkLogin } = useCheckLogin();
  const tip = useTips();
  const setPersonalStories = useSetRecoilState(personalStoriesAtom)
  const storyTags = useRecoilValue(storyTagsAtom);


  const fetchPersonalStories = async () => {
    const stories = await getStoriesInPublicGallery({
      access_token: localStorage.getItem('token'),
      status: 'All',
      tagNames: [...storyTags]
    });

    // 处理 Unknown 类型
    if (stories?.storyList) {
      stories.storyList = stories.storyList.map(story => {
        if (story.type === 'Unknown') {
          return {
            ...story,
            type: detectTypeFromUrl(story.url)
          };
        }
        return story;
      });
    }
    setPersonalStories(stories);
  };

  const handleMouseEnter = () => {
    if (inputRef.current) {
      setIsHovered(true);
    }
  }

  const handleCreateStory = async () => {
    const video_raw_url = inputRef.current?.value;
    if (!checkLogin()) return
    if (!video_raw_url) {
      tip({
        type: 'warning',
        content: 'Please enter a URL to be shared.',
      });
      return
    }

    // 判断是否为合法网址
    const isValidUrl = (url) => {
      try {
        new URL(url);
        return true;
      } catch (err) {
        return false;
      }
    }

    if (!isValidUrl(video_raw_url)) {
      tip({
        type: 'warning',
        content: 'Please enter a valid URL.',
      });
      return;
    }

    // 判断是否为支持的平台
    const supportedPlatforms = {
      'youtube.com': true,
      'youtu.be': true,
      'twitter.com': true,
      'x.com': true,
      'bilibili.com': true,
      'xiaohongshu.com': true,
      'reddit.com': true,
      'instagram.com': true,
    };

    const urlDomain = new URL(video_raw_url).hostname.replace('www.', '');
    if (!Object.keys(supportedPlatforms).some(domain => urlDomain.includes(domain))) {
      tip({
        type: 'warning',
        content: 'This platform is not supported yet. Please share content from YouTube, X(Twitter), Bilibili or Xiaohongshu.',
      });
      return;
    }

    try {
      setLoading(true);
      const result = await createStory(video_raw_url)
      if (!result?.error) {
        tip({
          type: 'success',
          content: 'Successfully created a story! Please wait for review.'
        });
      } else {
        throw new Error(result?.error)
      }
    } catch (error) {
      tip({
        type: 'error',
        content: 'Failed to create a story, Please check the URL and try again.'
      });
    } finally {
      fetchPersonalStories()
      setLoading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }

  const handleMouseLeave = () => {
    if (inputRef.current) {
      setIsHovered(false);
    }
  }

  useEffect(() => {
    if (isHovered) {
      focusTimeoutRef.current = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    } else {
      clearTimeout(focusTimeoutRef.current);
      inputRef.current?.blur();
    }
  }, [isHovered])

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`will-change-transform group/wrapper overflow-hidden flex-shrink-0 cursor-pointer [background:linear-gradient(98.88deg,rgba(70,151,157,0.2)_9.27%,rgba(86,84,197,0.2)_97.66%)] rounded-[12px] p-[12px_20px] w-full relative flex items-center justify-between
    ${styles.miniStoryPostWrapper}`}>
      <span className="group-hover/wrapper:opacity-0 opacity-100 transition-300-ease text-[14px] text-left [background-image:linear-gradient(90deg,#E9B664_44.89%,#8C76F0_92.99%)] [color:transparent] [-webkit-background-clip:text] font-bold">Share Stories Earn Credits!</span>
      <div className={`will-change-transform [background:linear-gradient(90deg,rgba(255,255,255,0)_9.32%,rgba(255,255,255,0.12)_100%)] [opacity:0.5] absolute top-[50%] right-[20px] [transform-origin:top_right] [transform:translateY(-50%)_rotate(-10deg)] w-[calc(100%-12px)] h-[calc(100%-12px)] flex items-center justify-end p-2 rounded-lg group-hover/wrapper:opacity-100 group-hover/wrapper:[transform:translateY(-50%)_rotate(0deg)] group-hover/wrapper:right-[6px] transition-300-ease`}>
        <input type="text" ref={inputRef} placeholder="Paste the link here" className="group-hover/wrapper:opacity-100 opacity-0 transition-300-ease w-full h-full bg-transparent outline-none text-white pl-[8px]" />
        <button onClick={handleCreateStory} className={`relative text-white rounded p-[4px_16px] text-[12px] font-bold hover:[box-shadow:0px_0px_12px_0px_rgba(255,206,115,0.25)] transition-300-ease [background:linear-gradient(98.88deg,#DEB05F_9.27%,#6765EC_97.66%)]`}>
          <img src={story_cursor} alt="story_cursor" className={`group-hover/wrapper:opacity-0 opacity-100 transition-300-ease w-[24px] h-auto absolute bottom-[-12px] right-1 `} />
          <span className={`transition-300-ease ${!loading ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>Share</span>
          <div className={`${loading ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-300-ease absolute-center`}>
            <CSSLoading />
          </div>
        </button>
      </div>
    </div>
  )
}

const StorySharingBonus = () => {
  const [personalStories, setPersonalStories] = useRecoilState(personalStoriesAtom);
  const [currentHoverItem, setCurrentHoverItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const logInfo = useRecoilValue(logInfoAtom);
  const wrapperRef = useRef(null);

  const NUM_DATA = useMemo(() => {
    const initData = [
      { name: "Pending", num: 0, list: [] },
      { name: "Approved", num: 0, list: [] },
      { name: "Rejected", num: 0, list: [] }
    ];

    const storyList = personalStories?.storyList || [];

    const statusCounts = storyList.reduce((acc, item) => {
      // 如果是type为Unknown的则跳过
      if (item.type === 'Unknown') return acc;
      if (!acc[item.status]) {
        acc[item.status] = {
          count: 0,
          list: []
        };
      }
      acc[item.status].count += 1;
      acc[item.status].list.push(item);
      return acc;
    }, {});

    return initData.map(item => ({
      ...item,
      num: (statusCounts[item.name]?.count || 0).toString().padStart(2, '0'),
      list: statusCounts[item.name]?.list || []
    }));
  }, [personalStories?.storyList]);

  const storyTags = useRecoilValue(storyTagsAtom);


  const fetchPersonalStories = useDebounce(async (storyTags) => {
    const stories = await getStoriesInPublicGallery({
      access_token: localStorage.getItem('token'),
      status: 'All',
      tagNames: [...storyTags]
    });

    // 处理 Unknown 类型
    if (stories?.storyList) {
      stories.storyList = stories.storyList.map(story => {
        if (story.type === 'Unknown') {
          return {
            ...story,
            type: detectTypeFromUrl(story.url)
          };
        }
        return story;
      });
    }
    setPersonalStories(stories);
  }, 100)

  useEffect(() => {
    if (logInfo) {
      fetchPersonalStories(storyTags);
    }
  }, []);

  const handleMouseEnter = (item) => {
    setCurrentPage(0);
    setCurrentHoverItem(item);
  }

  const handleMouseLeave = () => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      setCurrentPage(0);
      setCurrentHoverItem(null);
    }
  }

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  }

  const handleNextPage = () => {
    const maxPage = Math.ceil((currentHoverItem?.list?.length || 0) / 4) - 1;
    setCurrentPage(prev => Math.min(maxPage, prev + 1));
  }

  const getTypeIcon = (type, url = '') => {
    // 定义平台映射关系
    const platformMap = {
      'YouTube': {
        icon: story_youtube,
        urls: ['youtube.com', 'youtu.be']
      },
      'X': {
        icon: story_twitter,
        urls: ['twitter.com', 'x.com']
      },
      'Bilibili': {
        icon: story_bilibili,
        urls: ['bilibili.com']
      },
      'Xiaohongshu': {
        icon: story_xiaohongshu,
        urls: ['xiaohongshu.com']
      },
      'Instagram': {
        icon: story_ins,
        urls: ['instagram.com']
      },
      'Reddit': {
        icon: story_reddit,
        urls: ['reddit.com']
      }
    };

    // 如果type匹配则直接返回对应icon
    if (platformMap[type]) {
      return platformMap[type].icon;
    }

    // 根据url匹配平台
    const platform = Object.values(platformMap).find(p =>
      p.urls.some(u => url.includes(u))
    );

    return platform?.icon || null;
  }

  const currentPageItems = currentHoverItem?.list?.filter(item => item.type !== 'Unknown')?.slice(currentPage * 4, (currentPage + 1) * 4) || [];

  return (
    <div
      ref={wrapperRef}
      className="bg-[rgba(255,255,255,0.05)] rounded-[24px] p-3 w-[324px]"
      onMouseLeave={handleMouseLeave}
    >
      <div className='px-[18px]'>
        <div className='flex justify-between text-[14px] sm:text-[16px] items-center mb-[16px]'>
          <p className="text-[rgba(255,255,255,0.7)]">Story-sharing bonus</p>
        </div>
        <p className="text-[rgba(255,255,255,1)] font-bold text-[16px] sm:text-[18px] text-left">
          Check your <span className="[background-image:linear-gradient(90deg,#E9B664_44.89%,#8C76F0_92.99%)] [color:transparent] [-webkit-background-clip:text]">Story-sharing</span> bonus here!
        </p>
      </div>
      <div className={`flex justify-between gap-[6px] overflow-hidden transition-300-ease`}>
        {NUM_DATA.map((item, index) => (
          <div
            key={index}
            className='bg-[rgba(255,255,255,0.03)] rounded-xl p-3 flex-1 cursor-pointer transition-300-ease hover:bg-[rgba(255,255,255,0.06)]'
            onMouseEnter={() => handleMouseEnter(item)}
          >
            <span className='font-bold text-[42px] [background-image:linear-gradient(117.43deg,#FFC541_13.87%,#826FFF_80.43%)] [color:transparent] [-webkit-background-clip:text]'>{item.num}</span>
            <p className='font-bold text-xs text-white'>{item.name}</p>
          </div>
        ))}
      </div>
      <div className={`relative overflow-hidden ${currentHoverItem ? 'h-[184px] mt-3' : 'h-0'} [transition:all_0.3s_ease]`}>
        <TransitionGroup>
          <CSSTransition
            key={currentHoverItem?.name || 'empty'}
            classNames={{
              enter: 'opacity-0',
              enterActive: 'opacity-100 transition-opacity duration-300 delay-50',
              exit: 'opacity-0 transition-opacity duration-50'
            }}
            timeout={450}
            unmountOnExit
          >
            <div className={`absolute w-full bg-[rgba(0,0,0,0.1)] rounded-2xl h-full`}>
              <div className="w-full h-full p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className='flex items-center gap-2'>
                    <div className='[background:linear-gradient(180deg,#DFB060_0%,#756DDD_100%)] w-[3px] h-[16px] rounded-[41px]'></div>
                    <span className="font-bold text-white">{currentHoverItem?.name || 'Pending'} Links</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePrevPage}
                      className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] disabled:opacity-50 cursor-pointer transition-300-ease"
                      disabled={currentPage === 0}
                    >
                      <i className="w-[10px] h-[10px] block border-t-2 border-l-2 border-[rgba(255,255,255,0.6)] rotate-[-45deg] hover:border-white transition-colors"></i>
                    </button>
                    <button
                      onClick={handleNextPage}
                      className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] disabled:opacity-50 cursor-pointer transition-300-ease"
                      disabled={currentPage >= Math.ceil((currentHoverItem?.list?.length || 0) / 4) - 1}
                    >
                      <i className="w-[10px] h-[10px] block border-t-2 border-r-2 border-[rgba(255,255,255,0.6)] rotate-45 hover:border-white transition-colors"></i>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {currentPageItems.length ? currentPageItems.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-white text-sm">
                      <div className="flex items-center justify-center w-[42px]">
                        <img src={getTypeIcon(item.type, item.url)} alt={item.type} className="h-[20px] w-auto mx-auto" />
                      </div>
                      <a
                        title={item.url}
                        target="_blank"
                        rel="noreferrer"
                        href={item.url}
                        className="text-[rgba(255,255,255,0.6)] hover:text-[rgba(255,255,255,1)] transition-300-ease hover:underline truncate max-w-[200px] cursor-pointer"
                      >
                        {item.url}
                      </a>
                      <i>
                        {item.status === 'Pending' && <img src={story_pending} alt="story_pending" className={`w-[16px] h-auto ${styles['animate-story_pending-spin']}`} />}
                        {item.status === 'Approved' && <span className='text-[rgba(57,220,84,0.8)] font-bold [font-style:normal]'>+1</span>}
                        {item.status === 'Rejected' && <img src={story_rejected} alt="story_rejected" className="w-[16px] h-auto" />}
                      </i>
                    </div>
                  )) : (
                    <div className="flex flex-col gap-2 justify-center items-center h-full">
                      <img src={story_noresult} alt="story_noresult" className="w-[48px] h-auto" />
                      <span className="text-[#ffffff66] text-[14px]">No results found</span>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </div>
  )
}

export { StoryPost, StorySharingBonus, MiniStoryPost }